/* eslint-disable react-hooks/exhaustive-deps */
import { centerOfMass, featureCollection, length, point } from '@turf/turf';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { geometryTypes, sectionTypes } from '../../constants/fields';
import {
  largeCropPointRadius,
  mapLayers,
  mapModes,
  rotationCenterRadius,
  selectedRowPointRadius,
  smallCropPointRadius,
} from '../../constants/map';
import { MapContext } from '../../contexts/MapContext';
import { ItemContext } from '../../contexts/ItemContext';
import {
  colorForCrop,
  cropColorsMap,
  cropThemes,
  MAP_ACTIVE_COLOR,
} from '../../utils/colors';
import { emptyFeature, emptyGeojson } from '../../utils/geojson';
import { parseRowId } from '../../utils/field';
import { resetMapLayers } from '../../utils/map';
import { augmentFieldFeature } from '../../utils/layerAugmenters';

const getPointCircleRadius = (inRowSpacing) =>
  inRowSpacing == null || inRowSpacing > 8
    ? largeCropPointRadius
    : smallCropPointRadius;

const getActiveRowFeatures = (activeRowIndexes, rows) =>
  activeRowIndexes
    .map((rowId) => rows?.features?.[rowId])
    .filter((feature) => feature?.geometry?.coordinates)
    .map((feature) =>
      // Row length is zero, return single point; otherwise return feature
      length(feature) === 0 ? point(feature.geometry.coordinates[0]) : feature,
    );

const shouldEnableClickHandlers = (mapMode) =>
  // Enable click handlers in modes where the user
  // isn't actively editing something on the map.
  [
    mapModes.VIEW,
    mapModes.SECTION_ENGAGED,
    mapModes.ROW_ENGAGED,
    mapModes.MULTI_SECTION_SELECT,
    mapModes.SECTION_GROUP_ENGAGED,
  ].includes(mapMode);

const FieldSectionLayers = ({
  section,
  map,
  showCenter,
  showRotate,
  showOffset,
  readonly,
  debug,
  cropMap,
}) => {
  const {
    id,
    fieldFeature = emptyFeature(),
    points = emptyFeature(),
    rows,
    crop,
    rotationCenter = emptyFeature(),
    areaFraction,
    inRowSpacing,
    headlandWidth,
    headlandInnerFeature,
    type,
    name,
    allPoints = emptyFeature(),
    debugPoints = emptyFeature(),
    debugRows = emptyFeature(),
  } = section;

  const upToDateCrop = crop?.id && cropMap[crop?.id];
  const color = colorForCrop(upToDateCrop, cropThemes.LIGHT);
  const crops = useMemo(() => Object.values(cropMap), [cropMap]);

  const colorsDef = useMemo(
    () => [
      'match',
      ['get', 'cropName'],
      ...Object.entries(cropColorsMap(crops, cropThemes.LIGHT)).flat(),
      color, // Fallback if no color is specified at the row override level
    ],
    [crops, color],
  );

  const layerNames = useMemo(
    () =>
      Object.values(mapLayers).reduce(
        (result, layer) => Object.assign(result, { [layer]: `${id} ${layer}` }),
        {},
      ),
    [],
  );

  const { activeKey, setActiveKey, clearActiveKey } = useContext(ItemContext);
  const {
    sectionGroups: { getSectionGroup },
    mapState: {
      setMapMode,
      mapMode,
      context: {
        keylineMode = false,
        selectAllSections = false,
        hideIndividualLabels = false,
      },
      isActiveField,
      activeRowIndexes,
    },
  } = useContext(MapContext);
  // Use a ref for mapMode
  const mapModeRef = useRef();
  mapModeRef.current = mapMode;

  const hasSelectedRow = isActiveField({ id }) && activeRowIndexes.length > 0;
  const isEngaged =
    isActiveField({ id }) &&
    [
      mapModes.SECTION_ENGAGED,
      mapModes.MULTI_SECTION_SELECT,
      mapModes.SECTION_GROUP_ENGAGED,
    ].includes(mapMode);

  const inHoveredSectionGroup = useMemo(
    () => getSectionGroup(activeKey)?.sections.some((s) => s.id === id),
    [activeKey, getSectionGroup, id],
  );

  const hidePointsAndRows =
    isActiveField({ id }) && keylineMode && mapMode === mapModes.DRAW;

  const center =
    type === sectionTypes.GRID
      ? rotationCenter
      : [sectionTypes.IMPORTED, sectionTypes.KEYLINE].includes(type)
      ? centerOfMass(fieldFeature)
      : null;

  const onMouseMove = useCallback((e) => {
    if (
      e.features.length > 0 &&
      // Show hover state whenever click handlers would have an effect.
      shouldEnableClickHandlers(mapModeRef.current)
    ) {
      setActiveKey(id);
    }
  }, []);

  const onDblClick = useCallback((e) => {
    if (!shouldEnableClickHandlers(mapModeRef.current)) return;
    if (readonly) return;
    e.preventDefault();
    if (e.features.length > 0) {
      setMapMode(mapModes.EDIT, id);
    }
  }, []);

  const onClick = useCallback((e) => {
    if (!shouldEnableClickHandlers(mapModeRef.current)) return;
    e.preventDefault();
    // If command is pressed, add section to selection
    if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
      setMapMode(mapModes.MULTI_SECTION_SELECT, id, {
        selectActiveField: true,
      });
    } else if (e.features.length > 0) {
      setMapMode(mapModes.SECTION_ENGAGED, id);
    }
  }, []);

  useEffect(() => {
    const hoverLayer =
      type === sectionTypes.LINE ? layerNames.rows : layerNames.fill;

    // Set/clear active key when hovered.
    map.on('mousemove', hoverLayer, onMouseMove);
    map.on('mouseleave', hoverLayer, clearActiveKey);

    // Engage section on single click, or toggle multi-select on meta-click.
    map.on('click', hoverLayer, onClick);

    // Edit on double click.
    if (type !== sectionTypes.IMPORTED) {
      map.on('dblclick', hoverLayer, onDblClick);
    }

    return () => {
      map.off('mousemove', hoverLayer, onMouseMove);
      map.off('mouseleave', hoverLayer, clearActiveKey);
      map.off('click', hoverLayer, onClick);
      if (type !== sectionTypes.IMPORTED) {
        map.off('dblclick', hoverLayer, onDblClick);
      }
    };
  }, []);

  useEffect(() => {
    map.addSource(layerNames.fieldFeature, {
      type: 'geojson',
      data: augmentFieldFeature(section) || emptyFeature(),
    });
    map.addSource(layerNames.points, { type: 'geojson', data: points });
    map.addSource(layerNames.rows, { type: 'geojson', data: rows });
    map.addSource(layerNames.center, {
      type: 'geojson',
      data: center,
    });
    if (debug) {
      map.addSource(layerNames.allPoints, {
        type: 'geojson',
        data: allPoints,
      });
      map.addSource(layerNames.debugPoints, {
        type: 'geojson',
        data: debugPoints,
      });
      map.addSource(layerNames.debugRows, {
        type: 'geojson',
        data: debugRows,
      });
    }
    map.addSource(layerNames.activeRow, emptyGeojson());
    map.addSource(layerNames.activeRowPoint, emptyGeojson());
    map.addSource(layerNames.selectedRow, emptyGeojson());
    if (type !== sectionTypes.LINE) {
      map.addLayer(
        {
          id: layerNames.fill,
          type: 'fill',
          source: layerNames.fieldFeature,
          layout: {},
          paint: {
            'fill-color': '#000',
            'fill-opacity': [
              'case',
              // Do not fill inner features to avoid double shadow
              ['==', ['get', 'isInnerFeature'], true],
              0,
              0.35 * (areaFraction || 1),
            ],
          },
        },
        mapLayers.FILL,
      );
    }
    map.addLayer(
      {
        id: layerNames.points,
        type: 'circle',
        source: layerNames.points,
        paint: {
          'circle-radius': getPointCircleRadius(inRowSpacing),
          'circle-color': colorsDef,
        },
      },
      mapLayers.POINTS,
    );
    if (debug) {
      map.addLayer(
        {
          id: layerNames.allPoints,
          type: 'circle',
          source: layerNames.allPoints,
          paint: {
            'circle-radius': getPointCircleRadius(inRowSpacing),
            'circle-color': 'black',
          },
        },
        mapLayers.ALL_POINTS,
      );
      map.addLayer(
        {
          id: layerNames.debugPoints,
          type: 'circle',
          source: layerNames.debugPoints,
          paint: {
            'circle-radius': getPointCircleRadius(inRowSpacing),
            'circle-color': 'red',
          },
        },
        mapLayers.DEBUG_POINTS,
      );
      map.addLayer(
        {
          id: layerNames.debugRows,
          type: 'line',
          source: layerNames.debugRows,
          paint: {
            'line-color': 'red',
            'line-width': 3,
          },
        },
        mapLayers.DEBUG_ROWS,
      );
    }

    map.addLayer(
      {
        id: layerNames.activeRow,
        type: 'line',
        source: layerNames.activeRow,
        paint: {
          'line-color': MAP_ACTIVE_COLOR,
          'line-width': 3,
        },
        layout: {
          'line-cap': 'round',
        },
      },
      mapLayers.ACTIVE_ROW,
    );

    map.addLayer(
      {
        id: layerNames.activeRowPoint,
        type: 'circle',
        source: layerNames.activeRowPoint,
        paint: {
          'circle-color': MAP_ACTIVE_COLOR,
          'circle-radius': selectedRowPointRadius,
        },
      },
      mapLayers.ACTIVE_ROW_POINT,
    );

    map.addLayer(
      {
        id: layerNames.selectedRow,
        type: 'circle',
        source: layerNames.selectedRow,
        paint: {
          'circle-color': MAP_ACTIVE_COLOR,
          'circle-radius': selectedRowPointRadius,
        },
      },
      mapLayers.SELECTED_ROW,
    );

    map.addLayer(
      {
        id: layerNames.rows,
        type: 'line',
        source: layerNames.rows,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          visibility: hidePointsAndRows ? 'none' : 'visible',
        },
        paint: {
          'line-color': colorsDef,
          'line-width': 1,
        },
      },
      mapLayers.ROWS,
    );

    map.addLayer(
      {
        id: layerNames.activeRowArrow,
        type: 'symbol',
        source: layerNames.activeRow,
        layout: {
          'symbol-placement': 'line',
          'symbol-spacing': 50,
          'icon-image': 'chevron',
          'icon-size': 0.15,
          'icon-rotation-alignment': 'map',

          // Weirdly, if you don't include this, the icon will slowly fade in instead of just appearing:
          'icon-allow-overlap': true,
        },
        paint: {
          'icon-color': MAP_ACTIVE_COLOR,
        },
      },
      mapLayers.ACTIVE_ROW_ARROW,
    );

    map.addLayer(
      {
        id: layerNames.fieldFeature,
        type: 'line',
        source: layerNames.fieldFeature,
        layout: {
          visibility: 'none',
        },
        paint: {
          'line-color': color,
          'line-width': [
            'case',
            // Use smaller line width for inner features
            ['==', ['get', 'isInnerFeature'], true],
            1.5,
            2,
          ],
        },
      },
      mapLayers.FIELD_FEATURE,
    );

    map.addLayer(
      {
        id: layerNames.centerIcon,
        type: 'symbol',
        source: layerNames.center,
      },
      mapLayers.CENTER,
    );

    map.addLayer(
      {
        id: layerNames.center,
        type: 'circle',
        source: layerNames.center,
        paint: {
          'circle-radius': rotationCenterRadius,
          'circle-color': 'red',
        },
      },
      mapLayers.CENTER,
    );

    map.addLayer(
      {
        id: layerNames.name,
        type: 'symbol',
        source:
          type === sectionTypes.LINE
            ? layerNames.fieldFeature
            : layerNames.center,
        layout: {
          'text-field': name,
          'symbol-placement': type === sectionTypes.LINE ? 'line' : 'point',
          'text-allow-overlap': true,
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          visibility: 'none',
        },
        paint: {
          'text-halo-color': 'rgba(0,0,0,1)',
          'text-halo-width': 2,
          'text-color': '#fff',
        },
      },
      mapLayers.NAME,
    );

    map.setLayoutProperty(layerNames.centerIcon, 'icon-size', 0.5);

    return () => resetMapLayers(map, layerNames);
  }, []);

  useEffect(() => {
    map.setPaintProperty(layerNames.points, 'circle-color', colorsDef);
    map.setPaintProperty(layerNames.rows, 'line-color', colorsDef);
  }, [colorsDef]);
  useEffect(() => {
    map.setPaintProperty(
      layerNames.fieldFeature,
      'line-color',
      inHoveredSectionGroup || activeKey === id ? MAP_ACTIVE_COLOR : color,
    );
  }, [color, activeKey, inHoveredSectionGroup]);

  useEffect(() => {
    map.getSource(layerNames.points).setData(points || emptyFeature());
  }, [points]);
  useEffect(() => {
    map.getSource(layerNames.rows).setData(rows || emptyFeature());
  }, [rows]);
  useEffect(() => {
    map
      .getSource(layerNames.fieldFeature)
      .setData(augmentFieldFeature(section) || emptyFeature());
  }, [fieldFeature, headlandInnerFeature, headlandWidth]);
  useEffect(() => {
    map.getSource(layerNames.center).setData(center);
  }, [center]);
  useEffect(() => {
    if (debug) {
      map.getSource(layerNames.allPoints).setData(allPoints);
      map.getSource(layerNames.debugPoints).setData(debugPoints);
      map.getSource(layerNames.debugRows).setData(debugRows);
    }
  }, [allPoints, debugPoints, debugRows]);
  useEffect(() => {
    if (type !== sectionTypes.LINE) {
      map.setPaintProperty(
        layerNames.points,
        'circle-radius',
        getPointCircleRadius(inRowSpacing),
      );
    }
  }, [inRowSpacing]);

  useEffect(() => {
    map.setLayoutProperty(
      layerNames.centerIcon,
      'icon-image',
      showRotate ? 'rotate' : 'offset',
    );
    map.setLayoutProperty(
      layerNames.centerIcon,
      'visibility',
      showRotate || showOffset ? 'visible' : 'none',
    );
  }, [showOffset, showRotate]);

  useEffect(() => {
    map.setLayoutProperty(
      layerNames.center,
      'visibility',
      isActiveField({ id }) && mapMode === mapModes.ROTATE && !showRotate
        ? 'visible'
        : 'none',
    );
  }, [showCenter]);

  useEffect(() => {
    [layerNames.rows, layerNames.points].forEach((layerName) =>
      map.setLayoutProperty(
        layerName,
        'visibility',
        hidePointsAndRows ? 'none' : 'visible',
      ),
    );
  }, [hidePointsAndRows]);

  useEffect(() => {
    map.setLayoutProperty(layerNames.name, 'text-field', name);
  }, [name]);

  useEffect(() => {
    const { rowSection, rowIdx } = parseRowId(activeKey);
    const hasActiveRow = rowSection === id;
    // Note: activeKey here is the ItemContext active key, not the MapContext active field.
    const isSelected =
      activeKey === id ||
      inHoveredSectionGroup ||
      hasActiveRow ||
      isEngaged ||
      selectAllSections;
    // Show outline when drawing a keyline.
    const showOutline = isSelected || (keylineMode && isActiveField({ id }));

    map.setLayoutProperty(
      layerNames.fieldFeature,
      'visibility',
      showOutline ? 'visible' : 'none',
    );

    map.setLayoutProperty(
      layerNames.name,
      'visibility',
      isSelected && !hideIndividualLabels ? 'visible' : 'none',
    );

    // Indicate active row
    const activeRowFeature = getActiveRowFeatures([rowIdx], rows)[0];
    const activeRowPoint =
      activeRowFeature?.geometry?.type === geometryTypes.POINT;
    // Set the activeRow layer that is active
    map
      .getSource(
        activeRowPoint ? layerNames.activeRowPoint : layerNames.activeRow,
      )
      .setData(hasActiveRow ? activeRowFeature : emptyFeature());
    // Clear the activeRow layer that is inactive
    map
      .getSource(
        activeRowPoint ? layerNames.activeRow : layerNames.activeRowPoint,
      )
      .setData(emptyFeature());
  }, [
    activeKey,
    isEngaged,
    keylineMode,
    selectAllSections,
    hideIndividualLabels,
    rows?.features,
  ]);

  useEffect(() => {
    let selectedRowsFeature = emptyFeature();

    if (hasSelectedRow) {
      selectedRowsFeature = featureCollection(
        getActiveRowFeatures(activeRowIndexes, rows),
      );
    }

    map.getSource(layerNames.selectedRow).setData(selectedRowsFeature);
  }, [activeRowIndexes, hasSelectedRow, rows]);

  return null;
};

export default FieldSectionLayers;
